import React, { useEffect, useMemo, useState } from 'react';
import swal from 'sweetalert';
import AdminSidebar from '../components/AdminSidebar';
import { adminAPI } from '../config/api';
import { showAppToast } from '../utils/notifications';
import {
  formatMoney,
  formatRentalDate,
  getBookedFor,
  getBookedFrom,
  getDropoffTime,
  getRentalDays,
  getRentalLineTotal,
} from '../utils/rentalFields';

const bookingStatuses = [
  'Pending Review',
  'Booking Confirmed',
  'Checked Out',
  'Completed',
  'Cancelled',
];

const emptyBookingForm = {
  userId: '',
  productId: '',
  bookedFrom: '',
  bookedFor: '',
  dropoffTime: '10:00',
  bookingStatus: 'Pending Review',
};

function rentalDaysBetween(startDate, endDate) {
  if (!startDate || !endDate) return 1;
  const diff = new Date(endDate) - new Date(startDate);
  const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
  return Math.max(1, Number.isFinite(days) ? days : 1);
}

function AdminBookings({ products = [] }) {
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [rowEdits, setRowEdits] = useState({});
  const [formData, setFormData] = useState(emptyBookingForm);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All statuses');
  const [sortMode, setSortMode] = useState('Newest first');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [expandedBookingId, setExpandedBookingId] = useState(null);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const [bookingsResponse, usersResponse] = await Promise.all([
        adminAPI.getBookings(),
        adminAPI.getUsers(),
      ]);
      const nextBookings = bookingsResponse.data?.cartItems || [];
      setBookings(nextBookings);
      setTotalCost(bookingsResponse.data?.totalCost || 0);
      setUsers(usersResponse.data || []);
      setRowEdits(Object.fromEntries(nextBookings.map((booking) => [
        booking.id,
        {
          quantity: getRentalDays(booking),
          bookedFrom: getBookedFrom(booking),
          bookedFor: getBookedFor(booking),
          dropoffTime: getDropoffTime(booking) || '',
          bookingStatus: booking.bookingStatus || 'Pending Review',
        },
      ])));
    } catch (error) {
      console.error('Failed to load bookings:', error);
      showAppToast({ message: 'Failed to load bookings. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const filteredBookings = useMemo(() => {
    const term = searchTerm.toLowerCase();
    const nextBookings = bookings.filter((booking) => {
      const userName = `${booking.user?.firstName || ''} ${booking.user?.lastName || ''}`;
      return (
        !term ||
        booking.product?.name?.toLowerCase().includes(term) ||
        userName.toLowerCase().includes(term) ||
        booking.user?.email?.toLowerCase().includes(term) ||
        booking.bookingStatus?.toLowerCase().includes(term)
      );
    });

    return nextBookings
      .filter((booking) => (
        statusFilter === 'All statuses' ||
        (booking.bookingStatus || 'Pending Review') === statusFilter
      ))
      .sort((first, second) => {
        if (sortMode === 'Pickup date') {
          return new Date(getBookedFrom(first) || 0) - new Date(getBookedFrom(second) || 0);
        }
        if (sortMode === 'Highest value') {
          return getRentalLineTotal(second) - getRentalLineTotal(first);
        }
        return Number(second.id || 0) - Number(first.id || 0);
      });
  }, [bookings, searchTerm, sortMode, statusFilter]);

  const updateRowEdit = (bookingId, field, value) => {
    setRowEdits((current) => ({
      ...current,
      [bookingId]: {
        ...current[bookingId],
        [field]: value,
      },
    }));
  };

  const saveBooking = async (bookingId) => {
    const edit = rowEdits[bookingId];
    setSavingId(bookingId);
    try {
      await adminAPI.updateBooking(bookingId, {
        quantity: Number(edit.quantity || 1),
        bookedFrom: edit.bookedFrom,
        bookedFor: edit.bookedFor,
        dropoffTime: edit.dropoffTime,
        bookingStatus: edit.bookingStatus,
      });
      showAppToast({ message: 'Booking updated successfully', type: 'success', duration: 2400 });
      await loadBookings();
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to update booking', type: 'error' });
    } finally {
      setSavingId(null);
    }
  };

  const deleteBooking = async (bookingId) => {
    const confirmed = await swal({
      title: 'Delete Booking?',
      text: 'This removes the booking request from the admin workflow.',
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true,
    });
    if (!confirmed) return;

    setSavingId(bookingId);
    try {
      await adminAPI.deleteBooking(bookingId);
      showAppToast({ message: 'Booking deleted successfully', type: 'success', duration: 2400 });
      await loadBookings();
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to delete booking', type: 'error' });
    } finally {
      setSavingId(null);
    }
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    const quantity = rentalDaysBetween(formData.bookedFrom, formData.bookedFor);

    try {
      await adminAPI.createBooking({
        userId: Number(formData.userId),
        productId: Number(formData.productId),
        quantity,
        bookedFrom: formData.bookedFrom,
        bookedFor: formData.bookedFor,
        dropoffTime: formData.dropoffTime,
        bookingStatus: formData.bookingStatus,
      });
      showAppToast({ message: 'Booking created successfully', type: 'success', duration: 2400 });
      setFormData(emptyBookingForm);
      setShowCreateForm(false);
      await loadBookings();
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to create booking', type: 'error' });
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content px-4 py-8">
        <div className="mx-auto max-w-7xl">
          <div className="admin-header">
            <div>
              <p className="text-accent">Reservations</p>
              <h1 className="section-title text-4xl text-primary-theme">Booking Management</h1>
              <p className="text-secondary-theme">
                Manage booking requests, dates, lifecycle status, and rental totals.
              </p>
            </div>
            <div className="admin-header-actions">
              <div className="admin-booking-summary">
                <strong>{bookings.length}</strong>
                <span>Bookings</span>
                <strong>{formatMoney(totalCost)}</strong>
                <span>Active value</span>
              </div>
              <button
                type="button"
                className="btn-luxury admin-add-booking-button"
                aria-expanded={showCreateForm}
                onClick={() => setShowCreateForm((value) => !value)}
              >
                <i className={showCreateForm ? 'bi bi-dash-lg' : 'bi bi-plus-lg'} />
                {showCreateForm ? 'Close Form' : 'Add Booking'}
              </button>
            </div>
          </div>

          <section className="admin-bookings-manager card-luxury">
            <div className="admin-card-header admin-bookings-toolbar">
              <div>
                <p className="text-accent">Operations queue</p>
                <h2 className="section-title text-2xl text-primary-theme">All Bookings</h2>
              </div>
              <div className="admin-bookings-filters">
                <input
                  type="search"
                  placeholder="Search by user, vehicle, or status..."
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="admin-search compact"
                />
                <select
                  aria-label="Filter bookings by status"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option>All statuses</option>
                  {bookingStatuses.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
                <select
                  aria-label="Sort bookings"
                  value={sortMode}
                  onChange={(event) => setSortMode(event.target.value)}
                >
                  <option>Newest first</option>
                  <option>Pickup date</option>
                  <option>Highest value</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="admin-booking-empty">Loading bookings...</div>
            ) : (
              <div className="admin-booking-table-wrap">
                <table className="admin-booking-table">
                  <thead>
                    <tr>
                      <th>Booking</th>
                      <th>Account</th>
                      <th>Pickup</th>
                      <th>Dropoff</th>
                      <th>Days</th>
                      <th>Status</th>
                      <th>Total</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.map((booking) => {
                      const edit = rowEdits[booking.id] || {};
                      const userName = `${booking.user?.firstName || ''} ${booking.user?.lastName || ''}`.trim() || `User ${booking.userId || ''}`;
                      const expanded = expandedBookingId === booking.id;

                      return (
                        <React.Fragment key={booking.id}>
                          <tr className="admin-booking-record" data-testid={`admin-booking-row-${booking.id}`}>
                            <td>
                              <span className="admin-table-kicker">#{booking.id}</span>
                              <strong>{booking.product?.name || 'Vehicle'}</strong>
                              <small>{formatMoney(booking.product?.price)} / day</small>
                            </td>
                            <td>
                              <strong>{userName}</strong>
                              <small>{booking.user?.email || 'No email on record'}</small>
                            </td>
                            <td>
                              <label className="admin-inline-field">
                                <span className="sr-only">Pickup date</span>
                                <input
                                  aria-label={`Pickup date for booking ${booking.id}`}
                                  type="date"
                                  value={edit.bookedFrom || ''}
                                  onChange={(event) => updateRowEdit(booking.id, 'bookedFrom', event.target.value)}
                                />
                              </label>
                            </td>
                            <td>
                              <div className="admin-inline-stack">
                                <label className="admin-inline-field">
                                  <span className="sr-only">Dropoff date</span>
                                  <input
                                    aria-label={`Dropoff date for booking ${booking.id}`}
                                    type="date"
                                    value={edit.bookedFor || ''}
                                    onChange={(event) => updateRowEdit(booking.id, 'bookedFor', event.target.value)}
                                  />
                                </label>
                                <label className="admin-inline-field">
                                  <span className="sr-only">Dropoff time</span>
                                  <input
                                    aria-label={`Dropoff time for booking ${booking.id}`}
                                    type="time"
                                    value={edit.dropoffTime || ''}
                                    onChange={(event) => updateRowEdit(booking.id, 'dropoffTime', event.target.value)}
                                  />
                                </label>
                              </div>
                            </td>
                            <td>
                              <label className="admin-inline-field admin-inline-field--days">
                                <span className="sr-only">Booked days</span>
                                <input
                                  aria-label={`Booked days for booking ${booking.id}`}
                                  type="number"
                                  min="1"
                                  max="365"
                                  value={edit.quantity || 1}
                                  onChange={(event) => updateRowEdit(booking.id, 'quantity', event.target.value)}
                                />
                              </label>
                            </td>
                            <td>
                              <span className="admin-booking-status">{booking.bookingStatus || 'Pending Review'}</span>
                              <label className="admin-inline-field">
                                <span className="sr-only">Booking status</span>
                                <select
                                  aria-label={`Booking status for booking ${booking.id}`}
                                  value={edit.bookingStatus || 'Pending Review'}
                                  onChange={(event) => updateRowEdit(booking.id, 'bookingStatus', event.target.value)}
                                >
                                  {bookingStatuses.map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                  ))}
                                </select>
                              </label>
                            </td>
                            <td>
                              <strong>{formatMoney(getRentalLineTotal(booking))}</strong>
                              <small>{getRentalDays(booking)} day{getRentalDays(booking) === 1 ? '' : 's'}</small>
                            </td>
                            <td>
                              <div className="admin-booking-row-actions">
                                <button
                                  type="button"
                                  className="admin-icon-button"
                                  aria-label={`Preview booking ${booking.id}`}
                                  aria-expanded={expanded}
                                  onClick={() => setExpandedBookingId(expanded ? null : booking.id)}
                                >
                                  <i className={expanded ? 'bi bi-eye-slash' : 'bi bi-eye'} />
                                </button>
                                <button
                                  type="button"
                                  className="admin-icon-action admin-icon-action--save"
                                  disabled={savingId === booking.id}
                                  onClick={() => saveBooking(booking.id)}
                                  aria-label={`Save booking ${booking.id}`}
                                  title={`Save booking ${booking.id}`}
                                >
                                  <i className="bi bi-check2-circle" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  className="admin-icon-action admin-icon-action--danger"
                                  disabled={savingId === booking.id}
                                  onClick={() => deleteBooking(booking.id)}
                                  aria-label={`Delete booking ${booking.id}`}
                                  title={`Delete booking ${booking.id}`}
                                >
                                  <i className="bi bi-trash3-fill" aria-hidden="true" />
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expanded && (
                            <tr className="admin-booking-detail-row">
                              <td colSpan="8">
                                <div className="admin-booking-detail-grid">
                                  <span><strong>User:</strong> {userName}</span>
                                  <span><strong>Email:</strong> {booking.user?.email || 'No email on record'}</span>
                                  <span><strong>Vehicle:</strong> {booking.product?.name || 'Vehicle'}</span>
                                  <span><strong>Pickup:</strong> {formatRentalDate(getBookedFrom(booking))}</span>
                                  <span><strong>Dropoff:</strong> {formatRentalDate(getBookedFor(booking))} {getDropoffTime(booking) || ''}</span>
                                  <span><strong>Current total:</strong> {formatMoney(getRentalLineTotal(booking))}</span>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {!filteredBookings.length && (
                      <tr>
                        <td colSpan="8" className="admin-booking-empty">
                          {searchTerm || statusFilter !== 'All statuses'
                            ? 'No bookings found matching your filters'
                            : 'No bookings yet'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {showCreateForm && (
            <form className="admin-booking-create card-luxury" onSubmit={handleCreate} aria-label="Create admin booking">
              <div>
                <p className="text-accent">Create Booking</p>
                <h2 className="section-title text-2xl text-primary-theme">Add a booking for a verified user</h2>
              </div>
              <label>
                <span>New booking user</span>
                <select
                  aria-label="New booking user"
                  value={formData.userId}
                  onChange={(event) => setFormData((current) => ({ ...current, userId: event.target.value }))}
                  required
                >
                  <option value="">Select user</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.firstName} {user.lastName} - {user.email}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>New booking vehicle</span>
                <select
                  aria-label="New booking vehicle"
                  value={formData.productId}
                  onChange={(event) => setFormData((current) => ({ ...current, productId: event.target.value }))}
                  required
                >
                  <option value="">Select vehicle</option>
                  {products.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>New pickup date</span>
                <input
                  aria-label="New pickup date"
                  type="date"
                  value={formData.bookedFrom}
                  onChange={(event) => setFormData((current) => ({ ...current, bookedFrom: event.target.value }))}
                  required
                />
              </label>
              <label>
                <span>New dropoff date</span>
                <input
                  aria-label="New dropoff date"
                  type="date"
                  value={formData.bookedFor}
                  onChange={(event) => setFormData((current) => ({ ...current, bookedFor: event.target.value }))}
                  required
                />
              </label>
              <label>
                <span>New dropoff time</span>
                <input
                  aria-label="New dropoff time"
                  type="time"
                  value={formData.dropoffTime}
                  onChange={(event) => setFormData((current) => ({ ...current, dropoffTime: event.target.value }))}
                />
              </label>
              <label>
                <span>New booking status</span>
                <select
                  aria-label="New booking status"
                  value={formData.bookingStatus}
                  onChange={(event) => setFormData((current) => ({ ...current, bookingStatus: event.target.value }))}
                >
                  {bookingStatuses.map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </label>
              <button type="submit" className="btn-luxury">
                <i className="bi bi-check2-circle" aria-hidden="true" />
                Create Booking
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminBookings;
